import Axios from "axios";
import dayjs from "dayjs";
import React from "react";
import { DataTable } from "../data-table";
import { Pagniation } from "../pagination";
import { PhotoFilter, PhotoFilterState } from "./filter";
import { buildExportName } from "../../utils";

interface PhotoProps {}

interface State {
  compNo: string;
  iccNo: string;
  invType: string;
  month: string;

  data: any[];
  complaint?: Record<string, unknown>[];
  inv?: Record<string, unknown>[];
  attachments?: string[];

  loading: boolean;

  paginate: {
    currentPage: number;
    total: number;
    take: number;
  };
}

const take = 20;

const hintStyle: React.CSSProperties = {
  marginTop: 16,
  color: "var(--blue-300)",
};

export const CaseDetailsPage: React.FC<PhotoProps> = (props) => {
  const [state, setState] = React.useState<State>({
    compNo: "",
    iccNo: "",
    invType: "",
    month: dayjs().subtract(2, "month").format("YYYY-MM"),

    data: [] as any[],
    loading: false,

    paginate: {
      currentPage: 1,
      take,
      total: Infinity,
    },
  });

  React.useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [
    state.compNo,
    state.iccNo,
    state.invType,
    state.month,
    state.paginate.currentPage,
    state.paginate.take,
  ]);

  const fetch = () => {
    const { compNo, iccNo, invType, month, paginate } = state;

    setState((prev) => ({ ...prev, loading: true }));

    Axios.get<[any[], number]>("/complaint/photo", {
      params: {
        invType,
        iccNo,
        month,
        compNo,
        skip: (paginate.currentPage - 1) * paginate.take,
        take: paginate.take,
      },
    })
      .then((res) => {
        const [data, total] = res.data;

        setState((prev) => {
          // The filter moved on while this page was in flight - those rows no
          // longer belong on screen.
          const stale =
            prev.compNo !== compNo ||
            prev.iccNo !== iccNo ||
            prev.invType !== invType ||
            prev.month !== month;
          if (stale) return prev;

          return {
            ...prev,
            data,
            paginate: { ...prev.paginate, total },
          };
        });
      })
      .finally(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });
  };

  const fetchPhoto = (caseNo: string, compNo: string) => {
    Axios.get(`/complaint/photo/details`, {
      params: {
        iccNo: caseNo,
        compNo,
      },
    }).then((res) => {
      setState((prev) => ({
        ...prev,
        complaint: res.data[0],
        inv: res.data[1],
        attachments: res.data[2] ? res.data[2].attachments : null,
      }));
    });
  };

  const handleOnBounce = (value: PhotoFilterState) => {
    const { invType, month, iccNo, compNo } = value;

    setState((prev) => {
      // Nothing actually changed - this also covers the filter's own debounce
      // firing once on mount - so leave state alone and skip the refetch.
      if (
        prev.invType === invType &&
        prev.month === month &&
        prev.iccNo === iccNo &&
        prev.compNo === compNo
      ) {
        return prev;
      }

      return {
        ...prev,
        invType,
        month,
        iccNo,
        compNo,
        // A new search always starts from page one.
        paginate: { ...prev.paginate, currentPage: 1, total: Infinity },
        complaint: undefined,
        inv: undefined,
        attachments: undefined,
      };
    });
  };

  const onChange = (page: number, pageSize: number) => {
    setState((prev) => ({
      ...prev,
      paginate: { ...prev.paginate, currentPage: page, take: pageSize },
      complaint: undefined,
      inv: undefined,
      attachments: undefined,
    }));
  };

  return (
    <div className={`photo${state.complaint ? " details-open" : ""}`}>
      <div className="wrapper">
        <span className="title">complaint</span>
        <div className="container">
          <span className="title">search</span>

          <PhotoFilter
            compNo={state.compNo}
            iccNo={state.iccNo}
            month={state.month}
            invType={state.invType}
            onDebounce={handleOnBounce}
          />

          {!state.loading &&
            state.paginate.total !== Infinity &&
            state.data.length === 0 && (
              <p style={hintStyle}>No matching records.</p>
            )}

          {state.data.length > 0 && (
            <>
              <DataTable
                type="portal-datatable"
                exportName={buildExportName("complaint", "case", "photo", state.iccNo || state.compNo || state.invType, state.month)}
                columns={Object.keys(state.data[0]).map((row) => ({
                  title: row,
                  dataIndex: row,
                }))}
                data={state.data}
                onClick={(row, unselect) => {
                  if (unselect) {
                    setState((prev) => ({
                      ...prev,
                      inv: undefined,
                      complaint: undefined,
                      attachments: undefined,
                    }));
                  } else fetchPhoto(row.ICC_NO, row.COMP_NO);
                }}
              />

              <Pagniation
                total={state.paginate.total}
                currentPage={state.paginate.currentPage}
                pageSize={state.paginate.take}
                onChange={onChange}
              />
            </>
          )}
        </div>
      </div>

      <div className="details">
        {state.attachments && (
          <>
            <span className="subheader">detail</span>
            <div className="photos">
              {state.attachments.map((row) => (
                <img alt="" src={`http://wcis.1823.gov.hk/${row}`} key={row} />
              ))}
            </div>
          </>
        )}

        {state.complaint && (
          <DataTable
            exportName={buildExportName("complaint", "case", "detail", state.iccNo || state.compNo)}
            columns={Object.keys(state.complaint[0]).map((row) => ({
              title: row,
              dataIndex: row,
            }))}
            data={state.complaint}
          />
        )}

        {state.inv && (
          <DataTable
            exportName={buildExportName("complaint", "case", "inv", state.iccNo || state.compNo)}
            columns={Object.keys(state.inv[0]).map((row) => ({
              title: row,
              dataIndex: row,
            }))}
            data={state.inv}
          />
        )}
      </div>
    </div>
  );
};
